/**
 * PanchangaSection - Panchanga and Divisional Charts
 *
 * Sections:
 * 1. Basic Panchanga - All detailed Panchanga elements
 * 2. Solar & Time Data - Astronomical data
 * 3. D7 Chara Karaka - Seven significators
 * 4. D8 Ashtamsha Vara - Eight-fold division + Chakra toggle
 * 5. D12 Lakshmi Dwadashamsha - Twelve Lakshmi qualities
 *
 * Note: Place, Date, Time moved to ChartInfoSection
 * Business logic extracted to panchangaDataUtils.ts
 */

import { devError } from '../../../../utils/devLog';
import { getLocalStorageItem, setLocalStorageItem } from '../../../../utils/localStorageUtils';
import React, { useEffect, useRef, useState } from 'react';
import { JatakaResponse } from '../../../../models/api';
import { AccordionSection } from './AccordionSection';
import { AccordionItem } from './types';
import { LoadingState } from './LoadingState';
import { useMultipleCharts } from '../MultipleChartsContext';
import { DIVISIONAL_CHARTS, getDivisionalChartsWithToggles } from '../../../../config/divisionalCharts';
import { useDivisionalChakrasManager } from '../../../../hooks/DivisionalChakrasManager';
import { NakshatraAngaInfoTable } from './components/NakshatraAngaInfoTable';
import { NakshatramshaInfoTable } from './components/NakshatramshaInfoTable';
import { UniversalDivisionalToggle } from './components/UniversalDivisionalToggle';
import { EnhancedComponent } from './components/EnhancedComponent';
import { enhancedRegistry } from './registry/EnhancedSectionRegistry';
import { ExpandableLabel } from './ExpandableLabel';
import chakraInitializationManager from '../../../../services/ChakraInitializationManager';
import { SharedChartData, SharedViewMode } from '../../../../types/share.types';
import { HStack, Text } from '@chakra-ui/react';
import { useSectionState } from './hooks/useSectionState';
import { extractPanchangaData, generatePluginSections } from './panchangaDataUtils';

// Section registrations (auto-register on import)
import './sections/MuhurtaSection';
import './sections/NakshatraSection';
import './sections/NakshatraAngaSection';
import './sections/CharaKarakaSection';
import './sections/AshtamshaSection';
import './sections/LakshmiDwadashamshaSection';
import './sections/AhirbhutamshaSection';

// Tab type for Basic Panchanga / Solar & Time Data switcher
type PanchangaTab = 'basic-panchanga' | 'solar-time';

// Tab type for Divisional Charts (D7-D60)
type DivisionalTab = 'd7' | 'd8' | 'd12' | 'd20' | 'd24' | 'd27' | 'd30' | 'd40' | 'd60';

// Divisional tab labels for header
const DIVISIONAL_TAB_LABELS: { id: DivisionalTab; label: string }[] = [
    { id: 'd7', label: 'D7' },
    { id: 'd8', label: 'D8' },
    { id: 'd12', label: 'D12' },
    { id: 'd20', label: 'D20' },
    { id: 'd24', label: 'D24' },
    { id: 'd27', label: 'D27' },
    { id: 'd30', label: 'D30' },
    { id: 'd40', label: 'D40' },
    { id: 'd60', label: 'D60' },
];

// Storage keys for tab persistence
const PANCHANGA_TAB_KEY = 'hora-panchanga-tab';
const DIVISIONAL_TAB_KEY = 'hora-divisional-tab';

// Helper to load tab from localStorage
const loadPanchangaTab = (): PanchangaTab => {
    const saved = getLocalStorageItem<string | null>(PANCHANGA_TAB_KEY, null);
    if (saved === 'basic-panchanga' || saved === 'solar-time') return saved;
    return 'basic-panchanga';
};

const loadDivisionalTab = (): DivisionalTab => {
    const saved = getLocalStorageItem<string | null>(DIVISIONAL_TAB_KEY, null);
    if (saved && DIVISIONAL_TAB_LABELS.some(t => t.id === saved)) return saved as DivisionalTab;
    return 'd7';
};

export interface PanchangaSectionProps {
    apiResponse?: JatakaResponse | null;
    className?: string;
    shareMode?: boolean; // If true, make Chart name and Place read-only (for guests)
    sharedViewMode?: SharedViewMode;  // View mode for shared charts (author/guest)
    sharedChartData?: SharedChartData;  // Shared chart data with share_config
}

/**
 * Custom PanchangaDetail component with expandable sections (adapted from original)
 */
interface PanchangaDetailProps {
    item: AccordionItem;
    isExpanded: (sectionId: string) => boolean;
    toggleSection: (sectionId: string) => void;
    apiResponse?: JatakaResponse | null;
    chartId?: string | null;
    // Divisional settings from DivisionalChakrasManager
    divisionalSettings?: any;  // Full settings object for all divisionals
}

const PanchangaDetail: React.FC<PanchangaDetailProps> = React.memo(({
    item,
    isExpanded,
    toggleSection,
    apiResponse,
    divisionalSettings
}) => {
    // Group details by category: basic fields and detailed (expandable) sub-sections
    const basicDetails: { [key: string]: any } = {};
    const detailedSections: { [key: string]: any[] } = {};

    item.details.forEach((detail) => {
        if (detail.category === 'basic' || !detail.category) {
            basicDetails[detail.label] = detail;
        } else if (detail.category === 'detailed' && detail.parentSection) {
            if (!detailedSections[detail.parentSection]) {
                detailedSections[detail.parentSection] = [];
            }
            detailedSections[detail.parentSection].push(detail);
        }
    });

    return (
        <div className="planet-detail-container">
            <div className="planet-detail-content">
                {/* Render using original structure from dc5c830b */}
                {Object.entries(basicDetails).map(([label, detail]) => {
                    const detailedItems = detailedSections[label];
                    const hasDetails = detailedItems && detailedItems.length > 0;
                    const sectionId = `panchanga-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`;

                    // Handle Muhurta Chakra toggle
                    if (detail.type === 'toggle' && label === 'Muhurta Chakra') {
                        return (
                            <UniversalDivisionalToggle
                                key={label}
                                toggleLabel={label}
                                colorScheme="orange"
                                itemId="muhurta-chakra"
                                apiResponse={apiResponse}
                                externalSettings={divisionalSettings.muhurta}
                            />
                        );
                    }

                    // Handle Show Ashtamsha Chakra toggle
                    if (detail.type === 'toggle' && label === 'Show Ashtamsha Chakra') {
                        return (
                            <UniversalDivisionalToggle
                                key={label}
                                toggleLabel={label}
                                colorScheme="orange"
                                itemId="d8-ashtamsha"
                                apiResponse={apiResponse}
                                externalSettings={divisionalSettings?.d8}
                            />
                        );
                    }

                    // Hide field if value is placeholder "-" (hidden by share config)
                    if (detail.value === '-') {
                        return null;
                    }

                    // Regular fields with original expandable structure
                    // Auto-expand D sections or single-item sections
                    const isAutoExpanded = (item.id.startsWith('d') && item.id.includes('-')) || detailedItems.length === 1;
                    const isActuallyExpanded = isAutoExpanded || isExpanded(sectionId);

                    return (
                        <div key={label} className="planet-detail-expandable-container">
                            {/* D40, D24, D30, and D60 have nested structure, don't show label to avoid double nesting */}
                            {item.id !== 'd40-divisions' && item.id !== 'd24-divisions' && item.id !== 'd30-divisions' && item.id !== 'd60-karana-shashtyamsha' && (
                                <div className="planet-detail-row">
                                    {hasDetails ? (
                                        <ExpandableLabel
                                            label={`${label}:`}
                                            isExpanded={isActuallyExpanded}
                                            onToggle={() => toggleSection(sectionId)}
                                            ariaControls={`${sectionId}-subsection`}
                                        />
                                    ) : (
                                        <span className="planet-detail-label">{label}:</span>
                                    )}
                                    {detail.showValue !== false && (
                                        <span className="planet-detail-value">{detail.value}</span>
                                    )}
                                </div>
                            )}

                            {/* Show content if auto-expanded or user expanded */}
                            {hasDetails && isActuallyExpanded && (
                                <div
                                    className="planet-detail-subsection"
                                    id={`${sectionId}-subsection`}
                                    role="region"
                                    aria-labelledby={`${sectionId}-label`}
                                >
                                    {detailedItems.map((detailItem, index) => {
                                        // Check if this field is registered in enhancedRegistry
                                        if (apiResponse) {
                                            const registeredSections = enhancedRegistry.getByParent(label, apiResponse);
                                            const matchingSection = registeredSections.find(
                                                section => section.label === detailItem.label && section.type === detailItem.type
                                            );

                                            // If registered, use EnhancedComponent for rendering
                                            if (matchingSection) {
                                                return (
                                                    <EnhancedComponent
                                                        key={index}
                                                        config={matchingSection}
                                                        apiResponse={apiResponse}
                                                        divisionalSettings={divisionalSettings}
                                                    />
                                                );
                                            }
                                        }

                                        // Handle ALL divisional chakra toggles universally (D7, D8, etc.)
                                        if (detailItem.type === 'toggle') {
                                            // Check if this is a divisional chakra toggle
                                            const divisionalChart = getDivisionalChartsWithToggles().find(
                                                chart => chart.toggleConfig?.label === detailItem.label
                                            );

                                            if (divisionalChart && divisionalChart.toggleConfig) {
                                                // Get external settings based on item ID using universal approach
                                                const getExternalSettings = (itemId: string) => {
                                                    if (!divisionalSettings) return undefined;

                                                    const settingsMap: Record<string, any> = {
                                                        'd7-chara-karaka': divisionalSettings.d7,
                                                        'd8-ashtamsha': divisionalSettings.d8,
                                                        'd12-dwadashamsha': divisionalSettings.d12,
                                                        'd20-vimsamsha-guruvadi': divisionalSettings.d20,
                                                        'd24-divisions': divisionalSettings.d24,
                                                        'd30-divisions': divisionalSettings.d30,
                                                        'd40-divisions': divisionalSettings.d40,
                                                        'd60-karana-shashtyamsha': divisionalSettings.d60
                                                    };
                                                    return settingsMap[itemId];
                                                };

                                                return (
                                                    <UniversalDivisionalToggle
                                                        key={index}
                                                        toggleLabel={divisionalChart.toggleConfig.label}
                                                        colorScheme={divisionalChart.toggleConfig.colorScheme}
                                                        itemId={item.id}
                                                        apiResponse={apiResponse}
                                                        externalSettings={getExternalSettings(item.id)}
                                                    />
                                                );
                                            }
                                        }

                                        // Handle Nakshatra Anga table
                                        if (detailItem.type === 'table' && detailItem.label === 'Nakshatra Anga') {
                                            return (
                                                <div key={index}>
                                                    <NakshatraAngaInfoTable apiResponse={apiResponse} />
                                                </div>
                                            );
                                        }

                                        // Regular detailed fields - skip if label is empty (for D12 table placeholder)
                                        if (!detailItem.label) {
                                            return null;
                                        }

                                        return (
                                            <div key={index} className="planet-detail-row">
                                                <span className="planet-detail-label">{detailItem.label}:</span>
                                                <span className="planet-detail-value">{detailItem.value}</span>
                                            </div>
                                        );
                                    })}

                                    {/* Add divisional chart tables for charts WITHOUT toggles (D27) */}
                                    {(() => {
                                        const chart = DIVISIONAL_CHARTS.find(c => c.id === item.id);
                                        // Only render tables for charts WITHOUT toggleConfig
                                        // Charts WITH toggleConfig are now handled by UniversalDivisionalToggle
                                        if (chart?.tableComponent === 'NakshatramshaInfoTable') {
                                            return <NakshatramshaInfoTable apiResponse={apiResponse} />;
                                        }
                                        return null;
                                    })()}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
});

/**
 * Main PanchangaSection component - Clean Enhanced Version
 */
export const PanchangaSection: React.FC<PanchangaSectionProps> = ({
    apiResponse,
    className,
    shareMode = false,
    sharedViewMode = 'author',
    sharedChartData
}) => {
    const { activeChartId } = useMultipleCharts();

    // Tab state for Basic Panchanga / Solar & Time Data switcher (persisted)
    const [panchangaTab, setPanchangaTabState] = useState<PanchangaTab>(loadPanchangaTab);
    const setPanchangaTab = (tab: PanchangaTab) => {
        setPanchangaTabState(tab);
        setLocalStorageItem(PANCHANGA_TAB_KEY, tab);
    };

    // Tab state for Divisional Charts D7-D60 (persisted)
    const [divisionalTab, setDivisionalTabState] = useState<DivisionalTab>(loadDivisionalTab);
    const setDivisionalTab = (tab: DivisionalTab) => {
        setDivisionalTabState(tab);
        setLocalStorageItem(DIVISIONAL_TAB_KEY, tab);
    };

    // Centralized manager for ALL divisional chakra hooks (Muhurta, D7-D60)
    // Calls all hooks at top level, ensuring auto-restore works even when accordion sections collapsed
    const divisionalSettings = useDivisionalChakrasManager();

    // Chart IDs for events
    const chartId = activeChartId;

    // Track initialization state to prevent events during first load
    const isInitializedRef = useRef<boolean>(false);
    const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Initialize state management for this chart
    // In share mode, use short_code for localStorage key to sync between author/guest modes
    const storageKey = shareMode && sharedChartData?.short_code
        ? sharedChartData.short_code
        : (activeChartId || 'default');

    const { state, loadAccordionState, saveAccordionState, isLoading: isStateLoading } = useSectionState(
        storageKey,
        'panchanga'
    );

    // Default sections for new charts
    const DEFAULT_SECTIONS = ['basic-panchanga'];

    // Initialize state with defaults for new charts (only on first visit)
    React.useEffect(() => {
        if (isStateLoading || !storageKey) {
            return;
        }

        // Only apply defaults if user has NEVER interacted with this chart
        // If hasInteracted is true, respect their choice (even if all sections are closed)
        if (!state?.hasInteracted) {
            const savedSections = Array.from(loadAccordionState());

            // First visit: initialize with defaults
            if (savedSections.length === 0) {
                const defaultSections = new Set(DEFAULT_SECTIONS);
                saveAccordionState(defaultSections);
            }
        }
    }, [isStateLoading, state, storageKey, saveAccordionState, loadAccordionState]);

    // State for nested expandable sections with localStorage persistence
    const nestedStorageKey = `${storageKey}-nested`;

    const [nestedExpandedSections, setNestedExpandedSections] = React.useState<Set<string>>(() => {
        const saved = getLocalStorageItem<string[] | null>(nestedStorageKey, null);
        return saved ? new Set(saved) : new Set();
    });

    const isNestedExpanded = React.useCallback((sectionId: string) => {
        return nestedExpandedSections.has(sectionId);
    }, [nestedExpandedSections]);

    const toggleNestedSection = React.useCallback((sectionId: string) => {
        setNestedExpandedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }

            // Save to localStorage
            setLocalStorageItem(nestedStorageKey, Array.from(newSet));

            return newSet;
        });
    }, [nestedStorageKey]);

    // Wrap itemsMapper to include shared props via closure
    // IMPORTANT: Use useCallback with dependencies to trigger re-render when viewMode changes
    const itemsMapperWithSharedProps = React.useCallback((data: any) => {
        return generatePluginSections(data, sharedViewMode, sharedChartData);
    }, [sharedViewMode, sharedChartData]);

    // Initialization effect - mark as initialized after delay
    useEffect(() => {
        if (chartId) {
            // Reset initialization when chart changes
            isInitializedRef.current = false;

            // Mark as initialized after short delay to avoid initial events
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }

            initTimeoutRef.current = setTimeout(() => {
                isInitializedRef.current = true;
                // devLog(`🎯 PanchangaSection: Initialization complete for chart ${chartId}`);

                // Use ChakraInitializationManager to handle events (prevents duplicates)
                chakraInitializationManager.initializeChakraEvents(chartId, 'panchanga-section-init');
            }, 200); // 200ms delay after chart load
        }

        return () => {
            if (initTimeoutRef.current) {
                clearTimeout(initTimeoutRef.current);
            }
        };
    }, [chartId]);

    // Load expanded sections from state
    const defaultExpandedSections = React.useMemo(() => {
        // If still loading, wait
        if (isStateLoading) {
            return null;
        }

        // Load from state
        const savedSections = Array.from(loadAccordionState());

        // If user has never interacted AND no saved sections: apply defaults (first visit)
        // Otherwise: respect user's choice (even if all sections are closed)
        if (!state?.hasInteracted && savedSections.length === 0) {
            return DEFAULT_SECTIONS;
        }

        return savedSections;
    }, [isStateLoading, loadAccordionState, state]);

    // Wrapper for saveAccordionState
    // MUST be before any early returns (React Hooks rules)
    const handleAccordionChange = React.useCallback((expandedSections: Set<string>) => {
        // Save state without forcing any sections to stay open
        // User should be able to collapse all sections if they want
        saveAccordionState(expandedSections);
    }, [saveAccordionState]);

    // Safety checks
    if (!apiResponse) {
        return (
            <div className={className} style={{ padding: '16px', color: '#666' }}>
                No panchanga data available
            </div>
        );
    }

    // Wait for state to load before rendering accordion
    // This ensures defaultExpandedSections are ready on first render
    if (isStateLoading) {
        return <LoadingState message="Loading section state..." className={className} />;
    }

    // Custom header function - returns tab switcher for combined sections
    // isItemExpanded: if true, stop propagation (don't toggle); if false, let it propagate to expand
    const getCustomHeader = (item: AccordionItem, isItemExpanded: boolean) => {
        if (item.type === 'panchanga-combined') {
            return (
                <HStack spacing={0} fontSize="sm" fontWeight="medium">
                    <Text
                        cursor="pointer"
                        opacity={panchangaTab === 'basic-panchanga' ? 1 : 0.5}
                        onClick={(e) => {
                            if (isItemExpanded) e.stopPropagation();
                            setPanchangaTab('basic-panchanga');
                        }}
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.2s"
                    >
                        Basic Panchanga
                    </Text>
                    <Text mx={1} opacity={0.5}>/</Text>
                    <Text
                        cursor="pointer"
                        opacity={panchangaTab === 'solar-time' ? 1 : 0.5}
                        onClick={(e) => {
                            if (isItemExpanded) e.stopPropagation();
                            setPanchangaTab('solar-time');
                        }}
                        _hover={{ opacity: 0.8 }}
                        transition="opacity 0.2s"
                    >
                        Solar & Time
                    </Text>
                </HStack>
            );
        }
        if (item.type === 'divisional-combined') {
            return (
                <HStack spacing={0} fontSize="sm" fontWeight="medium" flexWrap="wrap">
                    {DIVISIONAL_TAB_LABELS.map((tab, index) => (
                        <React.Fragment key={tab.id}>
                            {index > 0 && <Text mx={1} opacity={0.5}>/</Text>}
                            <Text
                                cursor="pointer"
                                opacity={divisionalTab === tab.id ? 1 : 0.5}
                                onClick={(e) => {
                                    if (isItemExpanded) e.stopPropagation();
                                    setDivisionalTab(tab.id);
                                }}
                                _hover={{ opacity: 0.8 }}
                                transition="opacity 0.2s"
                            >
                                {tab.label}
                            </Text>
                        </React.Fragment>
                    ))}
                </HStack>
            );
        }
        return item.name;
    };

    return (
        <AccordionSection
            dataExtractor={extractPanchangaData}
            itemsMapper={itemsMapperWithSharedProps}
            defaultExpandedSections={defaultExpandedSections || []}
            loadingMessage="Loading Enhanced Panchanga..."
            idPrefix="enhanced-panchanga"
            apiResponse={apiResponse}
            className={className}
            onAccordionChange={handleAccordionChange}
            customDetailRenderer={(item: AccordionItem) => {
                // For combined panchanga section, render based on active tab
                if (item.type === 'panchanga-combined') {
                    if (panchangaTab === 'solar-time' && item.metadata?.solarTimeDetails) {
                        // Render Solar & Time Data (simple key-value list)
                        return (
                            <div className="planet-detail-container">
                                <div className="planet-detail-content">
                                    {item.metadata.solarTimeDetails.map((detail: any, index: number) => (
                                        <div key={index} className="planet-detail-row">
                                            <span className="planet-detail-label">{detail.label}:</span>
                                            <span className="planet-detail-value">{detail.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    }
                    // Default: render Basic Panchanga
                    return (
                        <div>
                            <PanchangaDetail
                                item={item}
                                isExpanded={isNestedExpanded}
                                toggleSection={toggleNestedSection}
                                apiResponse={apiResponse}
                                chartId={chartId}
                                divisionalSettings={divisionalSettings}
                            />
                        </div>
                    );
                }

                // For combined divisional section, render ALL tabs but hide inactive via CSS
                // This keeps UniversalDivisionalToggle mounted for Panel B event subscription
                if (item.type === 'divisional-combined' && item.metadata?.divisionalSections) {
                    return (
                        <div className="divisional-section">
                            {Object.entries(item.metadata.divisionalSections).map(([tabId, section]) => (
                                <div
                                    key={tabId}
                                    style={{ display: divisionalTab === tabId ? 'block' : 'none' }}
                                >
                                    <PanchangaDetail
                                        item={section as any}
                                        isExpanded={isNestedExpanded}
                                        toggleSection={toggleNestedSection}
                                        apiResponse={apiResponse}
                                        chartId={chartId}
                                        divisionalSettings={divisionalSettings}
                                    />
                                </div>
                            ))}
                        </div>
                    );
                }

                // For other sections, render normally
                return (
                    <div>
                        <PanchangaDetail
                            item={item}
                            isExpanded={isNestedExpanded}
                            toggleSection={toggleNestedSection}
                            apiResponse={apiResponse}
                            chartId={chartId}
                            divisionalSettings={divisionalSettings}
                        />
                    </div>
                );
            }}
            customHeader={getCustomHeader}
        />
    );
};

export default PanchangaSection;
